import { Box, Checkbox, Chip, FormControlLabel, IconButton, ListItem, Stack } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { ListItemBox } from "lib/@components/ListItemBox"
import { delay } from "lib/delay"
import { parentFind, parentTreeItem } from "library/tree/index.mjs"
import Iconify from "minimals-template/components/Iconify"
import { usePreviewSchedule } from "routes/schedule/preview-schedule"
import { useTreeItem } from "routes/schedule/lib/useTreeItem"
import { ManuallySelectedScheduleItem } from "./manual-mapping-slot-definitions"
import { Repeat } from "lib/@components/repeat.js"
import { SORT_FROM_CLASSIFICATION } from "library/SFG20/constants.mjs"
import { AssetMappingCriticalityIcons } from "routes/asset-register/plugins/asset-mapping/asset-mapping-criticality-icons.js"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { useEffect, useRef, useState } from "react"

const REMOVE_UNDO_TIMEOUT = 3000

const pendingRemovalSx = {
    mr: 1,
    borderRadius: 0.8,
    overflow: "hidden",
    position: "relative",
    display: "inline-flex",
    "&:before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        width: 0,
        backgroundColor: "rgba(25, 118, 210, 0.22)",
        pointerEvents: "none",
        "@keyframes manually-selected-schedule-remove-fill": {
            from: { width: "0%" },
            to: { width: "100%" },
        },
        animation: `${REMOVE_UNDO_TIMEOUT}ms linear 0ms 1 forwards manually-selected-schedule-remove-fill`,
    },
    "& .MuiFormControlLabel-label": {
        position: "relative",
        zIndex: 1,
        pr: 1,
    },
}

export function ManuallySelectedSchedules({ item: scheduleId, onRemove }) {
    const [pendingRemoval, setPendingRemoval] = useState(false)
    const onRemoveRef = useRef(onRemove)
    useEffect(updateRemoveRef, [onRemove])
    useEffect(handlePendingRemoval, [pendingRemoval, scheduleId])
    const treeItem = useTreeItem(scheduleId)
    const specialistParent = parentFind(treeItem, (entry) => entry[parentTreeItem]?.label === " SFG20")
    const specialistLabel = specialistParent?.label ?? "Specialist"
    const canPreview = !!treeItem && !treeItem.notLicensed
    const scheduleDisplay = `${treeItem?.label ?? `Schedule ${scheduleId}`}${treeItem?.code ? ` #${treeItem.code}` : ""}`
    const formControlLabelSx = pendingRemoval ? pendingRemovalSx : null

    const preview = usePreviewSchedule(treeItem?._id ?? scheduleId, {
        fullScreen: false,
        boost: false,
        sx: {
            my: 2,
            height: 1,
            flex: 1,
            alignSelf: "stretch",
            justifyContent: "stretch",
        },
    })
    const listOfCriticalities = Object.entries(treeItem?.$?.taskTypes ?? {}).sortBy(
        ([type]) => SORT_FROM_CLASSIFICATION[type]
    )

    return (
        <ListItem
            sx={{
                "&:last-of-type": {
                    mb: 1,
                },
            }}
        >
            <Stack sx={{ pr: 1 }}>
                <ListItemBox sx={{}}>
                    <Stack direction="row" alignItems="center" sx={{ width: 1 }}>
                        <FormControlLabel
                            sx={formControlLabelSx}
                            control={<Checkbox checked={!pendingRemoval} onClick={toggleRemoveState} />}
                            label={
                                <If value={pendingRemoval}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Box sx={{ color: "text.secondary" }}>{`Removing ${scheduleDisplay}`}</Box>
                                        <Chip
                                            aria-label="Undo schedule removal"
                                            onClick={toggleRemoveState}
                                            clickable
                                            color="primary"
                                            size="small"
                                            variant="outlined"
                                            icon={<Iconify icon="mdi:refresh" />}
                                            label="Undo"
                                        />
                                    </Stack>
                                    <Else>
                                        <Stack
                                            direction="row"
                                            sx={{ color: treeItem?.notLicensed ? "secondary.dark" : undefined }}
                                            spacing={1}
                                            alignItems="center"
                                        >
                                            <Box>
                                                {scheduleDisplay}
                                                {treeItem?.notLicensed && ` [${specialistLabel}]`}
                                            </Box>
                                            {!!listOfCriticalities.length && (
                                                <Chip
                                                    size="small"
                                                    label={
                                                        <Stack direction="row" spacing={0.6}>
                                                            <Repeat
                                                                list={listOfCriticalities}
                                                                item={<AssetMappingCriticalityIcons />}
                                                            />
                                                        </Stack>
                                                    }
                                                />
                                            )}
                                        </Stack>
                                    </Else>
                                </If>
                            }
                        />
                        <If value={pendingRemoval} not>
                            <Bound schedule={{ _id: scheduleId, label: treeItem?.label }}>
                                <ManuallySelectedScheduleItem.Slot>
                                    {canPreview && (
                                        <IconButton
                                            aria-label="Preview schedule"
                                            onClick={preview}
                                            color="primary"
                                            size="small"
                                        >
                                            <Iconify icon="mdi:eye" />
                                        </IconButton>
                                    )}
                                </ManuallySelectedScheduleItem.Slot>
                            </Bound>
                        </If>
                    </Stack>
                </ListItemBox>
            </Stack>
        </ListItem>
    )

    function handlePendingRemoval() {
        if (!pendingRemoval) return undefined
        let cancel = false
        void remove()
        return () => {
            cancel = true
        }
        async function remove() {
            try {
                await delay(REMOVE_UNDO_TIMEOUT)
                if (cancel) return
                await onRemoveRef.current(scheduleId)
            } catch (error) {
                if (cancel) return
                console.error("Schedule removal failed", error)
                setPendingRemoval(false)
            }
        }
    }

    function updateRemoveRef() {
        onRemoveRef.current = onRemove
    }

    function toggleRemoveState(event) {
        event?.preventDefault?.()
        event?.stopPropagation?.()
        setPendingRemoval((currentlyPendingRemoval) => !currentlyPendingRemoval)
    }
}
